import { useState } from "react";
import { FeatureLink as Link } from "@softmax/web-features/navigation";
import { communityNavigation } from "@softmax/web-features/community";
import { curatedText } from "@softmax/web-features/coworlds";
import { WikiView, type WikiViewProps } from "@softmax/web-ux/wiki";
import type {
  WikiPagePublic,
  WikiPageListPublic,
} from "@softmax/observatory-client/types";
import { useWikiEditor } from "./use-wiki-editor";
import { useWikiDeletion } from "./use-wiki-deletion";
import { useWikiRevert } from "./use-wiki-revert";
import { useWikiReads } from "./use-wiki-reads";
import { useWikiHashNavigation } from "./use-wiki-hash-navigation";

type WikiControllerProps = {
  elevatedPrivileges?: boolean;
  layout?: "page" | "embedded";
  coworld: string;
  wikiSlug: string;
  pageSlug: string;
  initialPage?: WikiPagePublic | null;
  initialIndex?: WikiPageListPublic;
  initialMissing?: boolean;
  initialDataUpdatedAt?: number;
  basePath: string;
  navigationSearch?: string;
};

export function WikiController(props: WikiControllerProps) {
  return (
    <WikiPageController
      key={`${props.wikiSlug}/${props.pageSlug}/${props.elevatedPrivileges === true}`}
      {...props}
    />
  );
}

function WikiPageController(props: WikiControllerProps) {
  const {
    elevatedPrivileges = false,
    layout = "page",
    wikiSlug,
    pageSlug,
    basePath,
    navigationSearch = "",
  } = props;
  const navigation = communityNavigation({ basePath, navigationSearch });
  const [mode, setMode] = useState<WikiViewProps["mode"]>("read");
  const [error, setError] = useState<string | null>(null);
  const [historyOpened, setHistoryOpened] = useState(false);
  const reads = useWikiReads({
    ...props,
    elevatedPrivileges,
    mode,
    historyOpened,
  });
  const identity = {
    wikiSlug,
    pageSlug,
    elevatedPrivileges,
    page: reads.page,
    accessLost: reads.accessLost,
    setMode,
    setError,
  };
  const { editor, changeMode } = useWikiEditor({ ...identity, mode });
  const { revertingId, revert } = useWikiRevert(identity);
  const deletion = useWikiDeletion(identity);
  useWikiHashNavigation({ page: reads.page, mode, pageSlug });

  return (
    <WikiView
      {...reads.states}
      layout={layout}
      pageSlug={pageSlug}
      displayTitle={
        reads.accessLost ? "Wiki unavailable" : curatedText(editor.draft.title)
      }
      wikiPageHref={navigation.wikiPageHref}
      mode={reads.accessLost ? "read" : mode}
      onModeChange={(next) => {
        if (next === "history") setHistoryOpened(true);

        changeMode(next);

        if (next === "history" && reads.historyFailed)
          void reads.refresh("history");
      }}
      onRefresh={reads.refresh}
      editor={editor}
      deletion={{
        open: deletion.confirmingDelete,
        reason: deletion.deleteReason,
        pending: deletion.deleting,
        onOpenChange: deletion.setConfirmingDelete,
        onReasonChange: deletion.setDeleteReason,
        onDelete: deletion.deletePage,
      }}
      mutationError={error}
      onDismissMutationError={() => setError(null)}
      revertingId={revertingId}
      onRevert={revert}
      renderLink={(linkProps) => {
        if (
          !navigationSearch ||
          !basePath ||
          !linkProps.href.startsWith(`${basePath}/`)
        )
          return <Link {...linkProps} />;

        return (
          <Link
            {...linkProps}
            href={navigation.wikiPageHref({
              relativeHref: linkProps.href.slice(basePath.length + 1),
            })}
          />
        );
      }}
    />
  );
}
